<template>
  <basic-page title="Harjoitussuunnitelma">
    <template v-slot:app-bar-buttons>
      <v-btn
        icon
        title="Harjoitussuunnitelmat"
        @click="$router.push({ name: 'Harjoitussuunnitelmat' })"
      >
        <v-icon>mdi-format-list-bulleted</v-icon>
      </v-btn>
      <v-tooltip bottom :disabled="canUseAiChat">
        <template v-slot:activator="{ on, attrs }">
          <div v-on="on" class="d-inline-block">
            <v-btn
              icon
              title="Avaa AI-chat"
              :disabled="!canUseAiChat"
              @click="aiChatDrawer = true"
              v-bind="attrs"
            >
              <v-icon>mdi-chat</v-icon>
            </v-btn>
          </div>
        </template>
        <span
          >Syötä harjoituksen aihe ja tallenna suunnitelma käyttääksesi
          AI-sparrausta.</span
        >
      </v-tooltip>
      <v-btn
        icon
        title="Tallenna harjoitussuunnitelma Firestoreen"
        :disabled="!hasTitle || saveLoading || !canWriteHarjoitussuunnitelma"
        :loading="saveLoading"
        @click="saveHarjoitussuunnitelma"
      >
        <v-icon>mdi-content-save</v-icon>
      </v-btn>
      <v-btn
        icon
        title="Poista harjoitussuunnitelma"
        :disabled="
          !harjoitussuunnitelmaId ||
          saveLoading ||
          !canWriteHarjoitussuunnitelma
        "
        @click="deleteHarjoitussuunnitelma"
      >
        <v-icon>mdi-delete</v-icon>
      </v-btn>
    </template>

    <template v-slot:right-drawer>
      <v-navigation-drawer
        v-model="aiChatDrawer"
        right
        temporary
        fixed
        :width="420"
        color="white"
        class="ai-chat-drawer-root"
      >
        <div class="ai-chat-drawer-content">
          <v-toolbar flat color="white" class="flex-grow-0">
            <responsive-toolbar-title> AI sparraus </responsive-toolbar-title>
            <v-btn icon @click="aiChatDrawer = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>

          <v-divider></v-divider>

          <div class="ai-chat-log pa-3">
            <div
              v-for="(m, idx) in aiChatMessages"
              :key="idx"
              class="ai-chat-row"
              :class="
                m.role === 'user' ? 'ai-chat-row-user' : 'ai-chat-row-assistant'
              "
            >
              <v-card outlined class="ai-chat-bubble pa-2">
                <div class="caption grey--text" v-if="m.role === 'user'">
                  Sinä
                </div>
                <div class="caption grey--text" v-else>AI</div>
                <div style="white-space: pre-line">{{ m.content }}</div>
              </v-card>
            </div>

            <div v-if="chatLoading" class="ai-chat-row ai-chat-row-assistant">
              <v-card outlined class="ai-chat-bubble pa-2">
                <div class="caption grey--text">AI</div>
                <div>Kirjoittaa...</div>
              </v-card>
            </div>
          </div>

          <v-divider></v-divider>

          <div class="ai-chat-footer pa-3">
            <v-textarea
              v-model="aiChatInput"
              label="Kirjoita viesti (esim. 'Täydennä päätavoite ja aikataulu')"
              outlined
              rows="6"
              no-resize
              class="ai-chat-input"
              :disabled="chatLoading || !canUseAiChat"
              @keydown.enter.exact.prevent="sendAiChat"
            ></v-textarea>
            <div class="d-flex justify-space-between">
              <v-btn
                text
                color="error"
                :disabled="chatLoading || !canUseAiChat"
                @click="resetAiChat"
              >
                Nollaa
              </v-btn>
              <v-btn
                color="primary"
                :loading="chatLoading"
                :disabled="!aiChatInput || chatLoading || !canUseAiChat"
                @click="sendAiChat"
              >
                Lähetä
              </v-btn>
            </div>
          </div>
        </div>
      </v-navigation-drawer>
    </template>

    <v-dialog v-model="createPlanDialog" max-width="640" persistent>
      <v-card>
        <v-card-title>Luo harjoitussuunnitelma tekoälyllä</v-card-title>
        <v-card-text>
          <v-alert dense text type="warning" class="mb-3">
            Tekoäly korvaa aiemmin syötetyt tiedot.
          </v-alert>
          <div class="mb-2">
            <div class="caption grey--text">Harjoituksen aihe</div>
            <div class="subtitle-1">{{ plan.title }}</div>
          </div>

          <v-textarea
            v-model="createPlanExtra"
            outlined
            auto-grow
            rows="3"
            label="Lisätiedot (taso, osallistujat, painopiste, erityisriskit, kalusto)"
            :disabled="aiLoading"
          ></v-textarea>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="aiLoading" @click="createPlanDialog = false">
            Peruuta
          </v-btn>
          <v-btn color="primary" :loading="aiLoading" @click="submitCreatePlan">
            Luo
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="createStationsDialog" max-width="640" persistent>
      <v-card>
        <v-card-title>Luo uudet rastit tekoälyllä</v-card-title>
        <v-card-text>
          <div class="mb-2">
            <div class="caption grey--text">Harjoituksen aihe</div>
            <div class="subtitle-1">{{ plan.title }}</div>
          </div>

          <v-select
            v-model="createStationsCount"
            :items="[1, 2, 3, 4]"
            label="Montako rastia luodaan"
            outlined
            dense
            :disabled="aiStationsLoading"
          />

          <v-textarea
            v-model="createStationsExtra"
            outlined
            auto-grow
            rows="3"
            label="Lisätiedot (taso, osallistujat, kalusto, rajaukset, turvallisuus)"
            :disabled="aiStationsLoading"
          ></v-textarea>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            :disabled="aiStationsLoading"
            @click="createStationsDialog = false"
          >
            Peruuta
          </v-btn>
          <v-btn
            color="primary"
            :loading="aiStationsLoading"
            :disabled="!hasTitle"
            @click="submitCreateStations"
          >
            Luo
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-container fluid>
      <v-row>
        <!-- Editor Column -->
        <v-col cols="12" md="6">
          <v-form ref="form" v-model="valid">
            <!-- 1. Perustiedot -->
            <v-card class="mb-4">
              <v-card-title>Perustiedot</v-card-title>
              <v-card-text>
                <v-text-field
                  v-model="plan.title"
                  label="Harjoituksen aihe"
                  outlined
                  dense
                >
                </v-text-field>
                <v-row>
                  <v-col cols="6">
                    <v-text-field
                      v-model="plan.date"
                      label="Päivämäärä"
                      type="date"
                      outlined
                      dense
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      v-model="plan.time"
                      label="Kellonaika / Kesto"
                      outlined
                      dense
                      hint="Esim. 18:00 - 20:00 tai 120 min"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="6">
                    <v-text-field
                      v-model="plan.location"
                      label="Paikka"
                      outlined
                      dense
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      v-model="plan.participantCount"
                      label="Osallistujamäärä"
                      outlined
                      dense
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-text-field
                  v-model="plan.trainer"
                  label="Kouluttaja"
                  outlined
                  dense
                ></v-text-field>

                <v-row>
                  <v-col cols="12" class="pt-0">
                    <div class="caption grey--text">Luoja</div>
                    <div>{{ creatorLabel }}</div>
                  </v-col>
                  <v-col cols="12" class="pt-0" v-if="isOwner">
                    <v-switch
                      v-model="plan.allowOthersEdit"
                      inset
                      dense
                      label="Salli muiden adminien muokata tätä harjoitussuunnitelmaa"
                      :disabled="saveLoading"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    class="pt-0"
                    v-else-if="plan && plan.allowOthersEdit === false"
                  >
                    <v-alert dense text type="info">
                      Luoja on estänyt muiden muokkauksen
                    </v-alert>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="6" class="pt-0">
                    <v-switch
                      v-model="autosaveEnabled"
                      inset
                      dense
                      label="Automaattitallennus"
                      :disabled="saveLoading || !canWriteHarjoitussuunnitelma"
                    />
                  </v-col>
                  <v-col cols="12" md="6" class="pt-0 d-flex align-center mb-3">
                    <div>
                      <div class="caption grey--text">Viimeksi tallennettu</div>
                      <div>{{ lastSavedLabel }}</div>
                    </div>
                  </v-col>
                </v-row>

                <v-row class="ma-0" align="center" justify="end">
                  <v-col cols="12" sm="6" md="4" class="pa-0 pr-sm-3">
                    <v-select
                      v-model="plan.aiModel"
                      :items="aiModelItems"
                      item-text="text"
                      item-value="value"
                      label="Tekoälymalli"
                      outlined
                      dense
                      hide-details
                      :disabled="aiLoading || aiStationsLoading || chatLoading"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                    md="8"
                    class="pa-0 d-flex justify-end"
                  >
                    <v-btn
                      small
                      color="primary"
                      outlined
                      class="create-plan-ai-btn"
                      :disabled="!hasBasicInfoForAiPlan || aiLoading"
                      :loading="aiLoading"
                      @click="openCreatePlanDialog"
                    >
                      <v-icon left small>mdi-robot</v-icon>
                      {{ createPlanButtonLabel }}
                    </v-btn>
                  </v-col>
                  <v-col cols="12" class="pa-0" v-if="!hasBasicInfoForAiPlan">
                    <div class="caption error--text text-right mt-1">
                      Syötä harjoituksen aihe ja paikka käyttääksesi tekoälyä.
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- 2. Tavoitteet ja Yksityiskohdat -->
            <v-card class="mb-4">
              <v-card-title>Tavoitteet ja Yksityiskohdat</v-card-title>
              <v-card-text>
                <v-textarea
                  v-model="plan.mainGoal"
                  label="Päätavoite"
                  outlined
                  rows="3"
                  auto-grow
                >
                  <template v-slot:append-outer>
                    <v-btn
                      icon
                      small
                      color="primary"
                      @click="generateAIContent('mainGoal')"
                      :disabled="!hasTitle"
                      :loading="aiFieldLoading === 'mainGoal'"
                    >
                      <v-icon>mdi-robot</v-icon>
                    </v-btn>
                  </template>
                </v-textarea>
                <v-textarea
                  v-model="plan.exerciseFlow"
                  label="Harjoituksen kulku"
                  outlined
                  rows="3"
                  auto-grow
                  hint="Esim. I. Valmistelu, II. Kierto..."
                >
                  <template v-slot:append-outer>
                    <v-btn
                      icon
                      small
                      color="primary"
                      @click="generateAIContent('exerciseFlow')"
                      :disabled="!hasTitle"
                      :loading="aiFieldLoading === 'exerciseFlow'"
                    >
                      <v-icon>mdi-robot</v-icon>
                    </v-btn>
                  </template>
                </v-textarea>
                <v-textarea
                  v-model="plan.resources"
                  label="Resurssit ja Kalusto"
                  outlined
                  rows="3"
                  auto-grow
                >
                  <template v-slot:append-outer>
                    <v-btn
                      icon
                      small
                      color="primary"
                      @click="generateAIContent('resources')"
                      :disabled="!hasTitle"
                      :loading="aiFieldLoading === 'resources'"
                    >
                      <v-icon>mdi-robot</v-icon>
                    </v-btn>
                  </template>
                </v-textarea>

                <v-subheader>Turvallisuus</v-subheader>
                <v-textarea
                  v-model="plan.safetyRisks"
                  label="Tunnistetut riskit"
                  outlined
                  rows="2"
                  auto-grow
                  hint="Esim. Liukastuminen, nostot, liikenne"
                >
                  <template v-slot:append-outer>
                    <v-btn
                      icon
                      small
                      color="primary"
                      @click="generateAIContent('safetyRisks')"
                      :disabled="!hasTitle"
                      :loading="aiFieldLoading === 'safetyRisks'"
                    >
                      <v-icon>mdi-robot</v-icon>
                    </v-btn>
                  </template>
                </v-textarea>
                <v-textarea
                  v-model="plan.safetyPpe"
                  label="Suojavarusteet"
                  outlined
                  rows="2"
                  auto-grow
                  hint="Esim. Kypärä, hanskat, polvisuojat"
                >
                  <template v-slot:append-outer>
                    <v-btn
                      icon
                      small
                      color="primary"
                      @click="generateAIContent('safetyPpe')"
                      :disabled="!hasTitle"
                      :loading="aiFieldLoading === 'safetyPpe'"
                    >
                      <v-icon>mdi-robot</v-icon>
                    </v-btn>
                  </template>
                </v-textarea>
                <v-textarea
                  v-model="plan.safetyInstructions"
                  label="Turvallisuusohjeet"
                  outlined
                  rows="2"
                  auto-grow
                  hint="Esim. Nostoergonomia, 'SEIS'-komento"
                >
                  <template v-slot:append-outer>
                    <v-btn
                      icon
                      small
                      color="primary"
                      @click="generateAIContent('safetyInstructions')"
                      :disabled="!hasTitle"
                      :loading="aiFieldLoading === 'safetyInstructions'"
                    >
                      <v-icon>mdi-robot</v-icon>
                    </v-btn>
                  </template>
                </v-textarea>
              </v-card-text>
            </v-card>

            <!-- 3. Vaarojen selvittäminen -->
            <v-card class="mb-4">
              <v-card-title>Vaarojen selvittäminen</v-card-title>
              <v-card-text>
                <risk-assessment
                  :value="plan.riskAssessmentList"
                  mode="planning"
                />
              </v-card-text>
            </v-card>

            <!-- 4. Rastit -->
            <v-card class="mb-4">
              <v-card-title class="d-flex justify-space-between">
                Rastit
                <div>
                  <v-btn
                    color="primary"
                    outlined
                    small
                    class="mr-2"
                    :disabled="!hasTitle || aiStationsLoading"
                    :loading="aiStationsLoading"
                    @click="openCreateStationsDialog"
                    ><v-icon left small>mdi-robot</v-icon>Luo rastit
                    tekoälyllä</v-btn
                  >
                  <v-btn color="primary" small @click="addStation"
                    >Lisää Rasti</v-btn
                  >
                </div>
              </v-card-title>
              <v-card-text>
                <v-expansion-panels accordion multiple v-model="stationsPanel">
                  <v-expansion-panel
                    v-for="(station, index) in plan.stations"
                    :key="index"
                  >
                    <v-expansion-panel-header>
                      {{ station.name || "Uusi Rasti" }}
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-text-field
                        v-model="station.name"
                        label="Rastin nimi"
                        outlined
                        dense
                      ></v-text-field>
                      <v-text-field
                        v-model="station.supervisor"
                        label="Valvoja(t)"
                        outlined
                        dense
                      ></v-text-field>
                      <v-textarea
                        v-model="station.equipment"
                        label="Välineet"
                        outlined
                        rows="2"
                        auto-grow
                      >
                        <template v-slot:append-outer>
                          <v-btn
                            icon
                            small
                            color="primary"
                            @click="
                              generateAIContent(
                                `stations[${index}].equipment`,
                                `Rasti: ${station.name}`,
                              )
                            "
                            :disabled="!hasTitle"
                            :loading="
                              aiFieldLoading === `stations[${index}].equipment`
                            "
                          >
                            <v-icon>mdi-robot</v-icon>
                          </v-btn>
                        </template>
                      </v-textarea>

                      <v-subheader class="pl-0">Tehtävät</v-subheader>
                      <v-row
                        v-for="(task, tIndex) in station.tasks"
                        :key="'t' + tIndex"
                        class="mb-2 align-center"
                        no-gutters
                      >
                        <v-col class="flex-grow-1 mr-2">
                          <v-textarea
                            v-model="station.tasks[tIndex]"
                            :label="'Tehtävä ' + (tIndex + 1)"
                            outlined
                            dense
                            hide-details
                            rows="1"
                            auto-grow
                          ></v-textarea>
                        </v-col>
                        <v-col cols="auto">
                          <v-btn
                            icon
                            color="error"
                            @click="removeTask(index, tIndex)"
                          >
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                        </v-col>
                      </v-row>
                      <v-btn
                        text
                        small
                        color="primary"
                        @click="addTask(index)"
                        class="pl-0"
                        >+ Lisää tehtävä</v-btn
                      >

                      <v-textarea
                        v-model="station.focusAreas"
                        label="Painopisteet"
                        outlined
                        rows="2"
                        class="mt-4"
                        auto-grow
                      >
                        <template v-slot:append-outer>
                          <v-btn
                            icon
                            small
                            color="primary"
                            @click="
                              generateAIContent(
                                `stations[${index}].focusAreas`,
                                `Rasti: ${station.name}`,
                              )
                            "
                            :disabled="!hasTitle"
                            :loading="
                              aiFieldLoading === `stations[${index}].focusAreas`
                            "
                          >
                            <v-icon>mdi-robot</v-icon>
                          </v-btn>
                        </template>
                      </v-textarea>

                      <v-btn
                        color="error"
                        text
                        @click="removeStation(index)"
                        class="mt-2"
                        >Poista Rasti</v-btn
                      >
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-card-text>
            </v-card>

            <!-- 4. Aikataulu -->
            <v-card class="mb-4">
              <v-card-title class="d-flex justify-space-between">
                Aikataulu
                <div>
                  <v-btn
                    color="primary"
                    outlined
                    small
                    class="mr-2"
                    :disabled="!hasTitle"
                    :loading="aiFieldLoading === 'schedule'"
                    @click="generateAIContent('schedule')"
                    ><v-icon left small>mdi-robot</v-icon>Luo aikataulu
                    tekoälyllä</v-btn
                  >
                  <v-btn color="primary" small @click="addScheduleItem"
                    >Lisää Rivi</v-btn
                  >
                </div>
              </v-card-title>
              <v-card-text>
                <!-- Desktop Header -->
                <v-row
                  v-if="$vuetify.breakpoint.smAndUp"
                  class="font-weight-bold mb-2"
                >
                  <v-col cols="2">Aika</v-col>
                  <v-col cols="7">Toiminta</v-col>
                  <v-col cols="2">Kesto (min)</v-col>
                  <v-col cols="1"></v-col>
                </v-row>

                <div
                  v-for="(item, index) in plan.schedule"
                  :key="index"
                  class="mb-4 mb-sm-0"
                >
                  <v-row align="center">
                    <!-- Time -->
                    <v-col cols="12" sm="2" class="pb-1 pb-sm-3 pt-0 pt-sm-3">
                      <div class="caption grey--text d-sm-none">Aika</div>
                      <v-text-field
                        v-if="index === 0"
                        v-model="plan.startTime"
                        type="time"
                        dense
                        outlined
                        hide-details
                      ></v-text-field>
                      <div v-else class="text-subtitle-1 py-2">
                        {{ calculatedSchedule[index].start }}
                      </div>
                    </v-col>

                    <!-- Activity -->
                    <v-col cols="12" sm="7" class="pb-1 pb-sm-3 pt-0 pt-sm-3">
                      <div class="caption grey--text d-sm-none">Toiminta</div>
                      <v-textarea
                        v-model="item.activity"
                        dense
                        outlined
                        hide-details
                        rows="1"
                        auto-grow
                      ></v-textarea>
                    </v-col>

                    <!-- Duration -->
                    <v-col cols="6" sm="2" class="pt-0 pt-sm-3">
                      <div class="caption grey--text d-sm-none">
                        Kesto (min)
                      </div>
                      <v-text-field
                        v-model="item.duration"
                        type="number"
                        dense
                        outlined
                        hide-details
                      >
                        <template
                          v-slot:append
                          v-if="!$vuetify.breakpoint.smAndUp"
                          >min</template
                        >
                      </v-text-field>
                    </v-col>

                    <!-- Delete -->
                    <v-col
                      cols="6"
                      sm="1"
                      class="d-flex justify-end justify-sm-center pt-0 pt-sm-3"
                    >
                      <v-btn
                        icon
                        color="error"
                        @click="removeScheduleItem(index)"
                        title="Poista"
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                  <v-divider class="d-sm-none mt-2"></v-divider>
                </div>
              </v-card-text>
            </v-card>

            <!-- 5. Yhteenveto -->
            <v-card class="mb-4">
              <v-card-title class="d-flex justify-space-between">
                Päätös
                <v-btn
                  color="primary"
                  outlined
                  small
                  :disabled="!hasTitle"
                  :loading="aiFieldLoading === 'jointConclusion'"
                  @click="generateAIContent('jointConclusion')"
                  ><v-icon left small>mdi-robot</v-icon>Luo päätös
                  tekoälyllä</v-btn
                >
              </v-card-title>
              <v-card-text>
                <v-textarea
                  v-model="plan.jointConclusion"
                  label="Päätös"
                  outlined
                  rows="3"
                  auto-grow
                >
                  <template v-slot:append-outer>
                    <v-btn
                      icon
                      small
                      color="primary"
                      @click="generateAIContent('jointConclusion')"
                      :disabled="!hasTitle"
                      :loading="aiFieldLoading === 'jointConclusion'"
                    >
                      <v-icon>mdi-robot</v-icon>
                    </v-btn>
                  </template>
                </v-textarea>
              </v-card-text>
            </v-card>

            <div class="d-flex justify-end">
              <v-btn
                color="primary"
                :loading="saveLoading"
                :disabled="
                  !hasTitle || saveLoading || !canWriteHarjoitussuunnitelma
                "
                @click="saveHarjoitussuunnitelma"
              >
                <v-icon left>mdi-content-save</v-icon>
                Tallenna
              </v-btn>
            </div>
          </v-form>
        </v-col>

        <!-- Preview Column -->
        <v-col cols="12" md="6">
          <v-card class="fill-height" outlined>
            <v-toolbar flat color="grey lighten-4">
              <responsive-toolbar-title> Esikatselu </responsive-toolbar-title>
              <v-btn
                icon
                :loading="pdfLoading"
                :disabled="pdfLoading"
                title="Lataa PDF"
                @click="exportPlanPdf"
              >
                <v-icon>mdi-file-pdf-box</v-icon>
              </v-btn>
              <v-btn icon @click="printPlan"
                ><v-icon>mdi-printer</v-icon></v-btn
              >
            </v-toolbar>
            <v-card-text id="print-area" class="black--text">
              <div class="text-h4 mb-2">
                {{ plan.title || "Harjoituksen aihe" }}
              </div>
              <div class="subtitle-1 mb-4">
                <strong>Aika:</strong> {{ plan.time }} |
                <strong>Paikka:</strong> {{ plan.location }} |
                <strong>Osallistujia:</strong> {{ plan.participantCount }} |
                <strong>Kouluttaja:</strong> {{ plan.trainer }}
              </div>

              <v-divider class="mb-4"></v-divider>

              <div class="text-h6">PÄÄTAVOITE</div>
              <p style="white-space: pre-line">{{ plan.mainGoal }}</p>

              <div v-if="plan.exerciseFlow">
                <div class="font-weight-bold">Harjoituksen kulku</div>
                <p style="white-space: pre-line">{{ plan.exerciseFlow }}</p>
              </div>

              <v-row>
                <v-col cols="6" v-if="plan.resources">
                  <div class="font-weight-bold">RESURSSIT</div>
                  <p style="white-space: pre-line">{{ plan.resources }}</p>
                </v-col>
                <v-col
                  cols="6"
                  v-if="
                    plan.safety ||
                    plan.safetyRisks ||
                    plan.safetyPpe ||
                    plan.safetyInstructions
                  "
                >
                  <div class="font-weight-bold">TURVALLISUUS</div>
                  <p v-if="plan.safety" style="white-space: pre-line">
                    {{ plan.safety }}
                  </p>

                  <div v-if="plan.safetyRisks">
                    <strong>Riskit:</strong>
                    <p style="white-space: pre-line">{{ plan.safetyRisks }}</p>
                  </div>
                  <div v-if="plan.safetyPpe">
                    <strong>Suojavarusteet:</strong>
                    <p style="white-space: pre-line">{{ plan.safetyPpe }}</p>
                  </div>
                  <div v-if="plan.safetyInstructions">
                    <strong>Ohjeet:</strong>
                    <p style="white-space: pre-line">
                      {{ plan.safetyInstructions }}
                    </p>
                  </div>
                </v-col>
              </v-row>

              <v-divider class="my-4"></v-divider>

              <div v-if="selectedRisks.length > 0">
                <div class="text-h6">VAAROJEN SELVITTÄMINEN</div>
                <div
                  v-for="(risk, index) in selectedRisks"
                  :key="index"
                  class="d-flex align-center my-1"
                >
                  <v-icon small color="black" class="mr-2"
                    >mdi-check-bold</v-icon
                  >
                  <span>{{ risk }}</span>
                </div>
              </div>

              <div v-if="plan.stations && plan.stations.length > 0">
                <div class="text-h5 mb-2">RASTIT & SISÄLTÖ</div>
                <v-row>
                  <v-col
                    v-for="(station, index) in plan.stations"
                    :key="index"
                    cols="12"
                    class="mb-3"
                  >
                    <v-card outlined>
                      <v-card-title
                        class="subtitle-1 font-weight-bold grey lighten-4 py-2"
                      >
                        {{ station.name }}
                      </v-card-title>
                      <v-card-text class="pt-2">
                        <div v-if="station.supervisor">
                          <strong>Valvoja:</strong> {{ station.supervisor }}
                        </div>
                        <div v-if="station.equipment">
                          <strong>Välineet:</strong>
                          <div style="white-space: pre-line">
                            {{ station.equipment }}
                          </div>
                        </div>

                        <div
                          v-if="station.tasks && station.tasks.length > 0"
                          class="mt-2"
                        >
                          <strong>Tehtävät:</strong>
                          <ol>
                            <li
                              v-for="(task, tIdx) in station.tasks"
                              :key="tIdx"
                            >
                              {{ task }}
                            </li>
                          </ol>
                        </div>

                        <div v-if="station.focusAreas" class="mt-2">
                          <strong>Painopisteet:</strong>
                          <div style="white-space: pre-line">
                            {{ station.focusAreas }}
                          </div>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </div>

              <div v-if="plan.jointConclusion" class="mt-4">
                <div class="text-h6">PÄÄTÖS</div>
                <p style="white-space: pre-line">{{ plan.jointConclusion }}</p>
              </div>

              <div
                v-if="plan.schedule && plan.schedule.length > 0"
                class="mt-4"
              >
                <div class="text-h6">AIKATAULU</div>
                <v-simple-table dense>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left">Aika</th>
                        <th class="text-left">Toiminta</th>
                        <th class="text-left">Kesto</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, idx) in calculatedSchedule" :key="idx">
                        <td>{{ item.range }}</td>
                        <td>{{ item.activity }}</td>
                        <td>{{ item.duration }} min</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </basic-page>
</template>

<script>
import Vue from "vue";
import BasicPage from "@/components/BasicPage.vue";
import ResponsiveToolbarTitle from "@/components/ResponsiveToolbarTitle.vue";
import dayjs from "dayjs";
import RiskAssessment from "@/components/RiskAssessment.vue";
import {
  getRiskAssessmentItems,
  processRiskAssessmentItems,
} from "@/assets/riskAssessmentItems";
import api from "@/api";
import { firestore as db, auth } from "@/plugins/firebase.app";
import { jsPDF } from "jspdf";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";

const createEmptyPlan = () => ({
  title: "",
  date: new Date().toISOString().substr(0, 10),
  time: "",
  location: "",
  participantCount: "",
  trainer: "",
  allowOthersEdit: true,
  aiModel: null,
  mainGoal: "",
  exerciseFlow: "",
  resources: "",
  safety: "",
  safetyRisks: "",
  safetyPpe: "",
  safetyInstructions: "",
  riskAssessmentList: processRiskAssessmentItems(getRiskAssessmentItems()),
  stations: [
    {
      name: "Rasti 1",
      supervisor: "",
      equipment: "",
      tasks: [""],
      focusAreas: "",
    },
  ],
  jointConclusion: "",
  startTime: "18:00",
  schedule: [{ activity: "Aloitus", duration: 15 }],
});

/**
 * @typedef {Object} RiskAssessmentItem
 * @property {string} id
 * @property {string} title
 * @property {boolean|null|number} done
 * @property {'switch'|'text'|'title'} [type]
 * @property {boolean} [value]
 * @property {string} [textValue]
 * @property {Object.<number, string>} [levels]
 * @property {string} [dependsOn]
 * @property {boolean} [required]
 * @property {'execution'|'planning'} [mode]
 * @property {RiskAssessmentItem[]} [children]
 * @property {number} [deepnessLevel]
 */

/**
 * @typedef {Object} Station
 * @property {string} name
 * @property {string} supervisor
 * @property {string} equipment
 * @property {string[]} tasks
 * @property {string} focusAreas
 */

/**
 * @typedef {Object} ScheduleItem
 * @property {string} activity
 * @property {number} duration
 * @property {string} [start]
 * @property {string} [end]
 * @property {string} [range]
 */

/**
 * @typedef {Object} Harjoitussuunnitelma
 * @property {string} title
 * @property {string} date
 * @property {string} time
 * @property {string} location
 * @property {string} participantCount
 * @property {string} trainer
 * @property {string|null} [aiModel]
 * @property {string} mainGoal
 * @property {string} exerciseFlow
 * @property {string} resources
 * @property {string} safety
 * @property {string} safetyRisks
 * @property {string} safetyPpe
 * @property {string} safetyInstructions
 * @property {RiskAssessmentItem[]} riskAssessmentList
 * @property {Station[]} stations
 * @property {string} jointConclusion
 * @property {string} startTime
 * @property {ScheduleItem[]} schedule
 */

export default Vue.extend({
  name: "Harjoitussuunnitelma",
  components: { BasicPage, RiskAssessment, ResponsiveToolbarTitle },
  data() {
    return {
      valid: true,
      saveLoading: false,
      pdfLoading: false,
      pdfLogoDataUrl: null,
      autoSaveIntervalId: null,
      autosaveEnabled: true,
      lastSavedAtLocal: null,
      harjoitussuunnitelmaId: null,
      aiLoading: false,
      aiFieldLoading: null,
      stationsPanel: [0],
      chatLoading: false,
      aiChatDrawer: false,
      aiChatInput: "",
      aiChatMessages: [],
      createPlanDialog: false,
      createPlanExtra: "",
      createStationsDialog: false,
      createStationsCount: 2,
      createStationsExtra: "",
      aiStationsLoading: false,
      plan: createEmptyPlan(),
    };
  },
  async created() {
    try {
      const saved = localStorage.getItem("hs_autosave_enabled");
      if (saved !== null) {
        this.autosaveEnabled = saved === "1";
      }
    } catch (e) {
      // ignore
    }

    const id = this.$route?.query?.id;
    if (id) {
      await this.loadHarjoitussuunnitelma(id);
    }
    await this.loadAiChat();

    // Autosave every 5 minutes (silent)
    this.autoSaveIntervalId = setInterval(() => {
      if (!this.autosaveEnabled) return;
      if (!this.canWriteHarjoitussuunnitelma) return;
      this.saveHarjoitussuunnitelma({ silent: true });
    }, 5 * 60 * 1000);
  },
  beforeDestroy() {
    if (this.autoSaveIntervalId) {
      clearInterval(this.autoSaveIntervalId);
      this.autoSaveIntervalId = null;
    }
  },
  computed: {
    aiModelItems() {
      return [
        {
          text: "Auto (gemini-3.5-flash-lite)",
          value: null,
        },
        {
          text: "gemini-3.5-flash-lite",
          value: "gemini-3.5-flash-lite",
        },
      ];
    },
    createPlanButtonLabel() {
      const isXs = Boolean(this.$vuetify?.breakpoint?.xsOnly);
      return isXs
        ? "Luo tekoälyllä"
        : "Luo harjoitussuunnitelma tekoälyllä perustiedoista";
    },
    hasTitle() {
      return Boolean((this.plan?.title || "").trim());
    },
    hasBasicInfoForAiPlan() {
      const titleOk = Boolean((this.plan?.title || "").trim());
      const locationOk = Boolean((this.plan?.location || "").trim());
      return titleOk && locationOk;
    },
    hasSavedHarjoitussuunnitelma() {
      return Boolean((this.harjoitussuunnitelmaId || "").trim());
    },
    canUseAiChat() {
      return this.hasTitle && this.hasSavedHarjoitussuunnitelma;
    },
    isAdmin() {
      try {
        return typeof this.$isAdmin === "function"
          ? this.$isAdmin()
          : Boolean(this.$isAdmin);
      } catch (e) {
        return false;
      }
    },
    ownerUid() {
      return this.plan?.ownerUid || null;
    },
    isOwner() {
      const uid = auth?.currentUser?.uid;
      if (!uid) return false;
      if (!this.ownerUid) return false;
      return uid === this.ownerUid;
    },
    creatorLabel() {
      const createdBy = this.plan?.createdBy;
      const nameFromDoc = createdBy?.user || this.plan?.ownerName;
      const emailFromDoc = createdBy?.email || this.plan?.ownerEmail;

      const name =
        nameFromDoc || (this.isOwner ? this.$userInfo?.displayName : null);
      const email =
        emailFromDoc || (this.isOwner ? this.$userInfo?.email : null);

      if (name && email) return `${name} (${email})`;
      if (name) return String(name);
      if (email) return String(email);
      if (this.plan?.ownerUid) return String(this.plan.ownerUid);
      return "";
    },
    canWriteHarjoitussuunnitelma() {
      if (!this.isAdmin) return false;
      if (!this.hasSavedHarjoitussuunnitelma) return true;
      if (this.isOwner) return true;
      return this.plan?.allowOthersEdit !== false;
    },
    calculatedSchedule() {
      let currentTime = dayjs(`2000-01-01T${this.plan.startTime}`);
      return this.plan.schedule.map((item) => {
        const start = currentTime.format("HH:mm");
        const duration = Number(item.duration) || 0;
        currentTime = currentTime.add(duration, "minute");
        const end = currentTime.format("HH:mm");
        return {
          ...item,
          start,
          end,
          range: `${start} – ${end}`,
        };
      });
    },
    lastSavedAt() {
      if (this.lastSavedAtLocal) return this.lastSavedAtLocal;
      const v = this.plan?.updatedAt;
      if (!v) return null;
      if (typeof v?.toDate === "function") return v.toDate();
      const d = new Date(v);
      if (!Number.isNaN(d.getTime())) return d;
      return null;
    },
    lastSavedLabel() {
      const dt = this.lastSavedAt;
      if (!dt) return "Ei tallennettu vielä";
      return dayjs(dt).format("D.M.YYYY HH:mm");
    },
    selectedRisks() {
      if (!this.plan.riskAssessmentList) return [];

      return this.plan.riskAssessmentList
        .filter(
          (item) =>
            item.mode !== "execution" &&
            item.type === "switch" &&
            item.done === true,
        )
        .map((item) => {
          return item.title.replace(/<[^>]*>?/gm, "");
        });
    },
  },
  methods: {
    resetToNew() {
      this.harjoitussuunnitelmaId = null;
      this.plan = createEmptyPlan();
      this.$set(this.plan, "ownerUid", null);
      this.$set(this.plan, "createdBy", null);
      this.$set(this.plan, "allowOthersEdit", true);
      this.stationsPanel = [0];
      this.aiChatMessages = [];
    },
    async loadHarjoitussuunnitelma(id) {
      try {
        const user = auth.currentUser;
        if (!user) {
          this.$toast.error("Kirjaudu sisään");
          return;
        }
        const snap = await getDoc(doc(db, "harjoitussuunnitelmat", String(id)));
        if (!snap.exists()) {
          this.$toast.error("Harjoitussuunnitelmaa ei löydy");
          return;
        }
        const data = snap.data() || {};

        this.harjoitussuunnitelmaId = snap.id;

        // Merge with defaults to keep structure intact
        const base = createEmptyPlan();
        const merged = { ...base, ...data };
        if (
          !Array.isArray(merged.riskAssessmentList) ||
          merged.riskAssessmentList.length === 0
        ) {
          merged.riskAssessmentList = base.riskAssessmentList;
        }
        if (!Array.isArray(merged.stations)) merged.stations = base.stations;
        if (!Array.isArray(merged.schedule)) merged.schedule = base.schedule;

        this.plan = merged;

        // Open first station if present
        if (
          Array.isArray(this.plan.stations) &&
          this.plan.stations.length > 0
        ) {
          this.stationsPanel = [0];
        }
      } catch (e) {
        console.error(e);
        this.$toast.error("Lataus epäonnistui");
      }
    },
    async deleteHarjoitussuunnitelma() {
      if (!this.harjoitussuunnitelmaId) return;
      if (!this.canWriteHarjoitussuunnitelma) {
        this.$toast.error(
          this.isAdmin
            ? "Luoja on estänyt muiden muokkauksen"
            : "Vain admin voi poistaa harjoitussuunnitelmia",
        );
        return;
      }
      const ok = confirm("Poistetaanko tämä harjoitussuunnitelma?");
      if (!ok) return;

      try {
        await deleteDoc(
          doc(db, "harjoitussuunnitelmat", this.harjoitussuunnitelmaId),
        );
        this.$toast.success("Poistettu");
        this.$router.push({ name: "Harjoitussuunnitelmat" });
      } catch (e) {
        console.error(e);
        this.$toast.error("Poisto epäonnistui");
      }
    },
    async saveHarjoitussuunnitelma(opts = {}) {
      const silent = Boolean(opts?.silent);
      if (!this.hasTitle) {
        if (!silent) this.$toast.error("Täytä ensin 'Harjoituksen aihe'");
        return;
      }
      if (!this.canWriteHarjoitussuunnitelma) {
        if (!silent)
          this.$toast.error(
            this.isAdmin
              ? "Luoja on estänyt muiden muokkauksen"
              : "Vain admin voi tallentaa harjoitussuunnitelmia",
          );
        return;
      }
      if (this.saveLoading) return;

      const user = auth.currentUser;
      if (!user) {
        if (!silent) this.$toast.error("Kirjaudu sisään tallentaaksesi");
        return;
      }

      this.saveLoading = true;
      try {
        // JSON round-trip strips undefined values (Firestore doesn't allow them)
        const planPayload = JSON.parse(JSON.stringify(this.plan || {}));

        const ownerUid = planPayload?.ownerUid || user.uid;
        const isCreating = !this.harjoitussuunnitelmaId;
        const allowOthersEditDefined =
          planPayload?.allowOthersEdit !== undefined;

        // Persist creator info as the FIRST saver/owner (and never overwrite it later)
        const creatorPatch = {};
        if (ownerUid === user.uid) {
          if (!planPayload?.createdBy) {
            creatorPatch.createdBy = this.$userDBContext || null;
          }
          if (!planPayload?.ownerName) {
            creatorPatch.ownerName = this.$userInfo?.displayName || null;
          }
          if (!planPayload?.ownerEmail) {
            creatorPatch.ownerEmail = this.$userInfo?.email || null;
          }
        }

        // Only write allowOthersEdit when:
        // - creating a new plan (default true), OR
        // - field already exists (legacy-safe), OR
        // - owner is editing and has explicitly set it.
        const allowOthersEditPatch = {};
        if (isCreating || allowOthersEditDefined || ownerUid === user.uid) {
          allowOthersEditPatch.allowOthersEdit =
            planPayload?.allowOthersEdit !== false;
        }

        const basePayload = {
          ...planPayload,
          ownerUid,
          ...allowOthersEditPatch,
          titleLower: String(planPayload?.title || "").toLowerCase(),
          updatedAt: serverTimestamp(),
          schemaVersion: 1,
        };

        if (!this.harjoitussuunnitelmaId) {
          const docRef = await addDoc(collection(db, "harjoitussuunnitelmat"), {
            ...basePayload,
            ...creatorPatch,
            createdAt: serverTimestamp(),
          });
          this.harjoitussuunnitelmaId = docRef.id;
          this.lastSavedAtLocal = new Date();
          this.$router.replace({
            name: "Harjoitussuunnitelma",
            query: { ...this.$route.query, id: docRef.id },
          });
          if (!silent) this.$toast.success("Harjoitussuunnitelma tallennettu");
        } else {
          await setDoc(
            doc(db, "harjoitussuunnitelmat", this.harjoitussuunnitelmaId),
            { ...basePayload, ...creatorPatch },
            { merge: true },
          );
          this.lastSavedAtLocal = new Date();
          if (!silent) this.$toast.success("Harjoitussuunnitelma päivitetty");
        }
      } catch (e) {
        console.error(e);
        if (!silent) {
          const code = e?.code ? String(e.code) : "";
          const msg = e?.message ? String(e.message) : "";
          this.$toast.error(
            code ? `Tallennus epäonnistui (${code})` : "Tallennus epäonnistui",
          );
          if (msg) {
            console.warn("Save error message:", msg);
          }
        }
      } finally {
        this.saveLoading = false;
      }
    },
    openCreatePlanDialog() {
      if (!this.hasBasicInfoForAiPlan) {
        this.$toast.error(
          "Täytä ensin ainakin 'Harjoituksen aihe' ja 'Paikka'",
        );
        return;
      }
      this.createPlanDialog = true;
    },
    async submitCreatePlan() {
      if (this.aiLoading) return;
      if (!this.hasBasicInfoForAiPlan) {
        this.$toast.error(
          "Täytä ensin ainakin 'Harjoituksen aihe' ja 'Paikka'",
        );
        return;
      }

      const parts = [];
      parts.push(`Harjoituksen aihe: ${String(this.plan.title).trim()}`);
      if ((this.plan.date || "").trim())
        parts.push(`Päivämäärä: ${String(this.plan.date).trim()}`);
      if ((this.plan.time || "").trim())
        parts.push(`Aika/kesto: ${String(this.plan.time).trim()}`);
      if ((this.plan.location || "").trim())
        parts.push(`Paikka: ${String(this.plan.location).trim()}`);
      if ((this.plan.participantCount || "").trim())
        parts.push(
          `Osallistujia: ${String(this.plan.participantCount).trim()}`,
        );
      if ((this.plan.trainer || "").trim())
        parts.push(`Kouluttaja: ${String(this.plan.trainer).trim()}`);

      const extra = (this.createPlanExtra || "").trim();
      const finalInstruction = extra
        ? `${parts.join(". ")}. Lisätiedot: ${extra}`
        : parts.join(". ");

      await this.generateFullPlan(finalInstruction);
    },
    async loadAiChat() {
      try {
        if (!this.canUseAiChat) return;
        const result = await api.harjoitussuunnitelmaAiChatGet({
          planId: this.harjoitussuunnitelmaId,
        });
        if (result.data.success) {
          this.aiChatMessages = Array.isArray(result.data.messages)
            ? result.data.messages
            : [];
        }
      } catch (e) {
        // Silent: chat is optional
        console.error(e);
      }
    },
    applyPlanPatch(patch) {
      const set = patch?.set && typeof patch.set === "object" ? patch.set : {};
      const updatedPaths = [];

      const setByPath = (rootObj, path, value) => {
        if (!path || typeof path !== "string") return;
        const arrayMatch = path.match(/^(\w+)\[(\d+)\]\.(\w+)$/);
        if (arrayMatch) {
          const [_, arrName, indexStr, prop] = arrayMatch;
          const idx = parseInt(indexStr);
          const arr = rootObj[arrName];
          if (Array.isArray(arr)) {
            if (!arr[idx]) {
              // create empty item if needed
              this.$set(arr, idx, {});
            }
            this.$set(arr[idx], prop, value);
          }
          return;
        }

        const arrayNoPropMatch = path.match(/^(\w+)\[(\d+)\]$/);
        if (arrayNoPropMatch) {
          const [_, arrName, indexStr] = arrayNoPropMatch;
          const idx = parseInt(indexStr);
          const arr = rootObj[arrName];
          if (Array.isArray(arr)) {
            this.$set(arr, idx, value);
          }
          return;
        }

        // top-level field
        this.$set(rootObj, path, value);
      };

      for (const [path, value] of Object.entries(set)) {
        setByPath(this.plan, path, value);
        updatedPaths.push(path);
      }

      if (updatedPaths.length > 0) {
        this.$toast.success(`Päivitin kenttiä: ${updatedPaths.join(", ")}`);
      }
    },
    async sendAiChat() {
      if (!this.hasTitle) {
        this.$toast.error("Täytä ensin 'Harjoituksen aihe'");
        return;
      }
      if (!this.hasSavedHarjoitussuunnitelma) {
        this.$toast.error(
          "Tallenna harjoitussuunnitelma ensin, jotta chat voidaan tallentaa tähän suunnitelmaan",
        );
        return;
      }
      const msg = (this.aiChatInput || "").trim();
      if (!msg || this.chatLoading) return;

      this.chatLoading = true;
      this.aiChatInput = "";
      this.aiChatMessages = [
        ...this.aiChatMessages,
        { role: "user", content: msg, ts: Date.now() },
      ];

      try {
        const payload = {
          planId: this.harjoitussuunnitelmaId,
          message: msg,
          planSnapshot: this.plan,
        };
        if (this.plan?.aiModel) payload.model = this.plan.aiModel;

        const result = await api.harjoitussuunnitelmaAiChatSend(payload);

        if (result.data.success) {
          if (Array.isArray(result.data.messages)) {
            this.aiChatMessages = result.data.messages;
          } else if (result.data.assistantText) {
            this.aiChatMessages = [
              ...this.aiChatMessages,
              {
                role: "assistant",
                content: result.data.assistantText,
                ts: Date.now(),
              },
            ];
          }

          if (result.data.patch) {
            this.applyPlanPatch(result.data.patch);
          }
        } else {
          this.$toast.error("Tekoälyvirhe: " + result.data.error);
        }
      } catch (e) {
        console.error(e);
        this.$toast.error("Virhe tekoälypyynnössä");
      } finally {
        this.chatLoading = false;
      }
    },
    async resetAiChat() {
      if (!this.hasTitle) {
        this.$toast.error("Täytä ensin 'Harjoituksen aihe'");
        return;
      }
      if (!this.hasSavedHarjoitussuunnitelma) {
        this.$toast.error(
          "Tallenna harjoitussuunnitelma ensin, jotta chat voidaan nollata tälle suunnitelmalle",
        );
        return;
      }
      if (this.chatLoading) return;
      const ok = confirm("Haluatko nollata tekoälykeskustelun?");
      if (!ok) return;
      this.chatLoading = true;
      try {
        const result = await api.harjoitussuunnitelmaAiChatReset({
          planId: this.harjoitussuunnitelmaId,
        });
        if (result.data.success) {
          this.aiChatMessages = [];
          this.$toast.success("Keskustelu nollattu");
        } else {
          this.$toast.error("Virhe: " + result.data.error);
        }
      } catch (e) {
        console.error(e);
        this.$toast.error("Virhe tekoälypyynnössä");
      } finally {
        this.chatLoading = false;
      }
    },
    openCreateStationsDialog() {
      if (!this.hasTitle) {
        this.$toast.error("Täytä ensin 'Harjoituksen aihe'");
        return;
      }
      this.createStationsDialog = true;
    },
    async submitCreateStations() {
      if (!this.hasTitle) {
        this.$toast.error("Täytä ensin 'Harjoituksen aihe'");
        return;
      }
      if (this.aiStationsLoading) return;

      const count = Math.max(1, Math.min(4, Number(this.createStationsCount)));
      const extra = (this.createStationsExtra || "").trim();

      this.aiStationsLoading = true;
      try {
        const payload = {
          planSnapshot: this.plan,
          count,
          instruction: extra,
        };
        if (this.plan?.aiModel) payload.model = this.plan.aiModel;

        const result = await api.harjoitussuunnitelmaGenerateStations(payload);

        if (result.data.success) {
          const stations = Array.isArray(result.data.stations)
            ? result.data.stations
            : [];

          if (!Array.isArray(this.plan.stations)) {
            this.$set(this.plan, "stations", []);
          }

          const startIndex = this.plan.stations.length;
          stations.forEach((s) => this.plan.stations.push(s));

          // Open newly added panels
          for (let i = startIndex; i < this.plan.stations.length; i++) {
            if (!this.stationsPanel.includes(i)) this.stationsPanel.push(i);
          }

          this.createStationsDialog = false;
          this.$toast.success("Uudet rastit luotu tekoälyllä");
        } else {
          this.$toast.error("Tekoälyvirhe: " + result.data.error);
        }
      } catch (e) {
        console.error(e);
        this.$toast.error("Virhe tekoälypyynnössä");
      } finally {
        this.aiStationsLoading = false;
      }
    },
    addStation() {
      this.plan.stations.push({
        name: `Rasti ${this.plan.stations.length + 1}`,
        supervisor: "",
        equipment: "",
        tasks: [""],
        focusAreas: "",
      });
      this.stationsPanel.push(this.plan.stations.length - 1);
    },
    removeStation(index) {
      this.plan.stations.splice(index, 1);
    },
    addTask(stationIndex) {
      this.plan.stations[stationIndex].tasks.push("");
    },
    removeTask(stationIndex, taskIndex) {
      this.plan.stations[stationIndex].tasks.splice(taskIndex, 1);
    },
    addScheduleItem() {
      this.plan.schedule.push({ activity: "", duration: 0 });
    },
    removeScheduleItem(index) {
      this.plan.schedule.splice(index, 1);
    },
    async generateAIContent(field, instruction = "") {
      if (!this.hasTitle) {
        this.$toast.error("Täytä ensin 'Harjoituksen aihe'");
        return;
      }
      this.aiFieldLoading = field;
      try {
        const payload = {
          currentPlan: this.plan,
          targetField: field,
          instruction: instruction,
        };
        if (this.plan?.aiModel) payload.model = this.plan.aiModel;

        const result = await api.generatePlanContent(payload);
        if (result.data.success) {
          if (field === "schedule") {
            try {
              let jsonText = String(result.data.text || "")
                .replace(/```json/g, "")
                .replace(/```/g, "");
              const parsed = JSON.parse(jsonText);
              const schedule = Array.isArray(parsed)
                ? parsed
                : Array.isArray(parsed?.schedule)
                ? parsed.schedule
                : [];

              const normalized = schedule
                .map((it) => ({
                  activity: String(it?.activity || "").trim(),
                  duration: Number(it?.duration || 0),
                }))
                .filter((it) => it.activity && it.duration > 0)
                .slice(0, 40);

              if (normalized.length === 0) {
                this.$toast.error("AI ei palauttanut kelvollista aikataulua");
                return;
              }

              this.$set(this.plan, "schedule", normalized);
              this.$toast.success("Aikataulu luotu tekoälyllä");
            } catch (parseError) {
              console.error("Schedule JSON Parse Error", parseError);
              this.$toast.error("Virhe aikataulun käsittelyssä");
            }
            return;
          }

          if (field.includes("[")) {
            const match = field.match(/(\w+)\[(\d+)\]\.(\w+)/);
            if (match) {
              const [_, arrayName, index, prop] = match;
              const idx = parseInt(index);
              const arr = this.plan[arrayName];
              if (arr && Array.isArray(arr) && arr[idx]) {
                this.$set(arr[idx], prop, result.data.text);
              }
            }
          } else {
            this.$set(this.plan, field, result.data.text);
          }
        } else {
          console.error(result.data.error);
          this.$toast.error("Tekoälyvirhe: " + result.data.error);
        }
      } catch (e) {
        console.error(e);
        this.$toast.error("Virhe tekoälypyynnössä");
      } finally {
        this.aiFieldLoading = null;
      }
    },
    async generateFullPlan(finalInstruction) {
      this.aiLoading = true;
      try {
        const payload = { instruction: finalInstruction };
        if (this.plan?.aiModel) payload.model = this.plan.aiModel;

        const result = await api.generatePlanContent(payload);
        if (result.data.success) {
          try {
            let jsonText = result.data.text
              .replace(/```json/g, "")
              .replace(/```/g, "");
            const newPlan = JSON.parse(jsonText);

            // Preserve Perustiedot fields (AI must not overwrite these)
            const preservedBasics = {
              title: this.plan.title,
              date: this.plan.date,
              time: this.plan.time,
              location: this.plan.location,
              participantCount: this.plan.participantCount,
              trainer: this.plan.trainer,
              aiModel: this.plan.aiModel,

              // Ownership / creator info shown inside Perustiedot
              ownerUid: this.plan.ownerUid,
              createdBy: this.plan.createdBy,
              ownerName: this.plan.ownerName,
              ownerEmail: this.plan.ownerEmail,
              allowOthersEdit: this.plan.allowOthersEdit,
            };

            // Preserve riskAssessmentList structure if not returned by AI
            const riskList = this.plan.riskAssessmentList;

            this.plan = { ...this.plan, ...newPlan, ...preservedBasics };

            if (!this.plan.riskAssessmentList) {
              this.plan.riskAssessmentList = riskList;
            }

            this.createPlanDialog = false;
            this.$toast.success("Suunnitelma luotu tekoälyllä");
          } catch (parseError) {
            console.error("JSON Parse Error", parseError);
            this.$toast.error("Virhe vastauksen käsittelyssä");
          }
        } else {
          this.$toast.error("Tekoälyvirhe: " + result.data.error);
        }
      } catch (e) {
        console.error(e);
        this.$toast.error("Virhe tekoälypyynnössä");
      } finally {
        this.aiLoading = false;
      }
    },
    printPlan() {
      const printArea = document.getElementById("print-area");
      if (!printArea) return;
      const printContent = printArea.innerHTML;
      const originalContent = document.body.innerHTML;

      // Simple print hack
      const printWindow = window.open("", "", "height=600,width=800");
      if (!printWindow) return;
      printWindow.document.write(
        "<html><head><title>Harjoitussuunnitelma</title>",
      );
      printWindow.document.write(
        '<link href="https://cdn.jsdelivr.net/npm/@mdi/font@6.x/css/materialdesignicons.min.css" rel="stylesheet">',
      );
      printWindow.document.write(
        "<style>body{font-family: sans-serif;} table {width: 100%; border-collapse: collapse;} th, td {border: 1px solid #ddd; padding: 8px; text-align: left;} .v-card {border: 1px solid #ccc; padding: 10px; margin-bottom: 10px;}</style>",
      );
      printWindow.document.write("</head><body>");
      printWindow.document.write(printContent);
      printWindow.document.write("</body></html>");
      printWindow.document.close();
      printWindow.print();
    },

    loadImageAsDataUrl(url) {
      return new Promise((resolve, reject) => {
        try {
          const img = new Image();
          // Same-origin URL (public/) is expected; keep crossOrigin for safety.
          img.crossOrigin = "anonymous";
          img.onload = () => {
            try {
              const canvas = document.createElement("canvas");
              canvas.width = img.naturalWidth || img.width;
              canvas.height = img.naturalHeight || img.height;
              const ctx = canvas.getContext("2d");
              ctx.drawImage(img, 0, 0);
              const dataUrl = canvas.toDataURL("image/png");
              resolve(dataUrl);
            } catch (e) {
              reject(e);
            }
          };
          img.onerror = (e) => reject(e);
          img.src = url;
        } catch (e) {
          reject(e);
        }
      });
    },

    async getPdfLogoDataUrl() {
      if (this.pdfLogoDataUrl) return this.pdfLogoDataUrl;

      const base =
        typeof process !== "undefined" && process.env && process.env.BASE_URL
          ? process.env.BASE_URL
          : "/";

      // Prefer same-origin icons in public/ to avoid CORS issues.
      const candidates = [
        `${base}img/icons/apple-touch-icon-180x180.png`,
        `${base}img/icons/apple-touch-icon.png`,
        // Fallback to the website logo URL (may fail due to CORS)
        "https://noormarkunvpk.fi/wp-content/uploads/2017/03/logo.png",
      ];

      for (const url of candidates) {
        try {
          const dataUrl = await this.loadImageAsDataUrl(url);
          if (dataUrl) {
            this.pdfLogoDataUrl = dataUrl;
            return dataUrl;
          }
        } catch (e) {
          // try next
        }
      }
      return null;
    },

    async exportPlanPdf() {
      if (this.pdfLoading) return;

      this.pdfLoading = true;
      try {
        const plan = this.plan || {};
        const doc = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });

        const marginX = 12;
        const marginY = 12;
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const maxWidth = pageWidth - marginX * 2;
        const lineHeight = 5;

        let y = marginY;

        const normalizePdfText = (value) => {
          if (value === null || value === undefined) return "";
          let s = String(value);
          // Normalize common weird spaces
          s = s.replace(/\u00A0/g, " ");
          // Fix "R a s t i"-style spacing (single-letter tokens separated by spaces)
          // Remove spaces that occur between two single-character alnum tokens.
          // Repeat once to handle longer sequences reliably.
          const singleTokenSpace =
            /(\b[A-Za-zÀ-ÖØ-öø-ÿ0-9])\s+(?=[A-Za-zÀ-ÖØ-öø-ÿ0-9]\b)/g;
          s = s.replace(singleTokenSpace, "$1");
          s = s.replace(singleTokenSpace, "$1");
          return s;
        };

        const logoDataUrl = await this.getPdfLogoDataUrl();
        if (logoDataUrl) {
          try {
            // Place logo top-right
            const logoSize = 18; // mm
            doc.addImage(
              logoDataUrl,
              "PNG",
              pageWidth - marginX - logoSize,
              marginY,
              logoSize,
              logoSize,
            );
          } catch (e) {
            // ignore logo failures
          }
        }

        const ensureSpace = (need = lineHeight) => {
          if (y + need > pageHeight - marginY) {
            doc.addPage();
            y = marginY;
          }
        };

        const addHeading = (text) => {
          ensureSpace(8);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(13);
          const lines = doc.splitTextToSize(normalizePdfText(text), maxWidth);
          for (const line of lines) {
            ensureSpace(7);
            doc.text(line, marginX, y);
            y += 7;
          }
          y += 1;
        };

        const addLabelValue = (label, value) => {
          const v = normalizePdfText(value).trim();
          if (!v) return;
          ensureSpace(lineHeight);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(11);
          doc.text(`${normalizePdfText(label)}:`, marginX, y);
          doc.setFont("helvetica", "normal");
          const lines = doc.splitTextToSize(v, maxWidth - 42);
          let first = true;
          for (const line of lines) {
            ensureSpace(lineHeight);
            doc.text(line, marginX + 42, y);
            if (first) first = false;
            y += lineHeight;
          }
        };

        const addParagraph = (text) => {
          const t = normalizePdfText(text).trim();
          if (!t) return;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(11);
          const lines = doc.splitTextToSize(t, maxWidth);
          for (const line of lines) {
            ensureSpace(lineHeight);
            doc.text(line, marginX, y);
            y += lineHeight;
          }
          y += 2;
        };

        // Header
        const creatorName =
          normalizePdfText(
            plan?.createdBy?.user || plan?.ownerName || "",
          ).trim() ||
          normalizePdfText(
            plan?.createdBy?.email || plan?.ownerEmail || "",
          ).trim();

        addHeading("Noormarkun VPK, harjoitussuunnitelma");
        if (creatorName) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(11);
          const creatorLine = `Luoja: ${creatorName}`;
          const lines = doc.splitTextToSize(creatorLine, maxWidth);
          for (const line of lines) {
            ensureSpace(lineHeight);
            doc.text(line, marginX, y);
            y += lineHeight;
          }
          y += 2;
        }

        const title = normalizePdfText(
          plan.title || "Harjoitussuunnitelma",
        ).trim();
        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        const titleLines = doc.splitTextToSize(title, maxWidth);
        for (const line of titleLines) {
          ensureSpace(7);
          doc.text(line, marginX, y);
          y += 7;
        }
        y += 2;

        addLabelValue("Päivä", plan.date);
        addLabelValue("Aika / kesto", plan.time);
        addLabelValue("Paikka", plan.location);
        addLabelValue("Osallistujia", plan.participantCount);
        addLabelValue("Kouluttaja", plan.trainer);
        y += 2;

        addHeading("Päätavoite");
        addParagraph(plan.mainGoal);

        if (String(plan.exerciseFlow || "").trim()) {
          addHeading("Harjoituksen kulku");
          addParagraph(plan.exerciseFlow);
        }

        if (String(plan.resources || "").trim()) {
          addHeading("Resurssit");
          addParagraph(plan.resources);
        }

        if (
          String(plan.safety || "").trim() ||
          String(plan.safetyRisks || "").trim() ||
          String(plan.safetyPpe || "").trim() ||
          String(plan.safetyInstructions || "").trim()
        ) {
          addHeading("Turvallisuus");
          addParagraph(plan.safety);
          if (String(plan.safetyRisks || "").trim()) {
            addLabelValue("Riskit", plan.safetyRisks);
          }
          if (String(plan.safetyPpe || "").trim()) {
            addLabelValue("Suojavarusteet", plan.safetyPpe);
          }
          if (String(plan.safetyInstructions || "").trim()) {
            addLabelValue("Ohjeet", plan.safetyInstructions);
          }
          y += 2;
        }

        if (
          Array.isArray(this.selectedRisks) &&
          this.selectedRisks.length > 0
        ) {
          addHeading("Vaarojen selvittäminen");
          doc.setFont("helvetica", "normal");
          doc.setFontSize(11);
          for (const risk of this.selectedRisks) {
            const lines = doc.splitTextToSize(`- ${risk}`, maxWidth);
            for (const line of lines) {
              ensureSpace(lineHeight);
              doc.text(line, marginX, y);
              y += lineHeight;
            }
          }
          y += 2;
        }

        if (Array.isArray(plan.stations) && plan.stations.length > 0) {
          addHeading("Rastit");
          for (const station of plan.stations) {
            const name = normalizePdfText(station?.name || "Rasti").trim();
            doc.setFont("helvetica", "bold");
            doc.setFontSize(12);
            const nameLines = doc.splitTextToSize(name, maxWidth);
            for (const line of nameLines) {
              ensureSpace(6);
              doc.text(line, marginX, y);
              y += 6;
            }
            doc.setFont("helvetica", "normal");
            doc.setFontSize(11);

            addLabelValue("Valvoja", station?.supervisor);
            addLabelValue("Välineet", station?.equipment);

            if (Array.isArray(station?.tasks) && station.tasks.length > 0) {
              const tasks = station.tasks.filter((t) => String(t || "").trim());
              if (tasks.length > 0) {
                ensureSpace(lineHeight);
                doc.setFont("helvetica", "bold");
                doc.text("Tehtävät:", marginX, y);
                y += lineHeight;
                doc.setFont("helvetica", "normal");
                for (const t of tasks) {
                  const lines = doc.splitTextToSize(
                    `• ${normalizePdfText(t).trim()}`,
                    maxWidth - 4,
                  );
                  for (const line of lines) {
                    ensureSpace(lineHeight);
                    doc.text(line, marginX + 4, y);
                    y += lineHeight;
                  }
                }
              }
            }

            if (String(station?.focusAreas || "").trim()) {
              addLabelValue("Painopisteet", station.focusAreas);
            }
            y += 2;
          }
        }

        if (
          Array.isArray(this.calculatedSchedule) &&
          this.calculatedSchedule.length > 0
        ) {
          addHeading("Aikataulu");
          doc.setFont("helvetica", "normal");
          doc.setFontSize(11);
          for (const item of this.calculatedSchedule) {
            const range = normalizePdfText(item?.range || "").trim();
            const activity = normalizePdfText(item?.activity || "").trim();
            const duration = normalizePdfText(item?.duration ?? "").trim();
            const line = `${range}  ${activity}${
              duration ? ` (${duration} min)` : ""
            }`;
            const lines = doc.splitTextToSize(line, maxWidth);
            for (const l of lines) {
              ensureSpace(lineHeight);
              doc.text(l, marginX, y);
              y += lineHeight;
            }
          }
          y += 2;
        }

        if (String(plan.jointConclusion || "").trim()) {
          addHeading("Päätös");
          addParagraph(plan.jointConclusion);
        }

        const safeName = (s) =>
          String(s || "")
            .trim()
            .replace(/[\\/:*?"<>|]+/g, "-")
            .replace(/\s+/g, " ")
            .slice(0, 80);

        const filename =
          safeName(`${plan.date || ""} ${title}`) || "harjoitussuunnitelma";
        doc.save(`${filename}.pdf`);
      } catch (e) {
        console.error(e);
        this.$toast.error("PDF-vienti epäonnistui");
      } finally {
        this.pdfLoading = false;
      }
    },
  },
  watch: {
    autosaveEnabled: function (newVal) {
      try {
        localStorage.setItem("hs_autosave_enabled", newVal ? "1" : "0");
      } catch (e) {
        // ignore
      }
    },
    "$route.query.id": async function (newId, oldId) {
      if (newId === oldId) return;
      if (!newId) {
        this.resetToNew();
        return;
      }
      await this.loadHarjoitussuunnitelma(newId);
      await this.loadAiChat();
    },
    "plan.title": function (newVal) {
      const has = Boolean((newVal || "").trim());
      if (!has) {
        this.aiChatDrawer = false;
        this.createPlanDialog = false;
        this.createStationsDialog = false;
      } else {
        if (
          this.canUseAiChat &&
          (!this.aiChatMessages || this.aiChatMessages.length === 0)
        ) {
          this.loadAiChat();
        }
      }
    },
  },
});
</script>

<style scoped>
::v-deep .ai-chat-drawer-root {
  height: 100vh !important;
  max-height: 100vh !important;
}

::v-deep .ai-chat-drawer-root .v-navigation-drawer__content {
  display: flex !important;
  flex-direction: column !important;
  height: 100% !important;
  max-height: 100% !important;
  overflow: hidden !important;
}

.ai-chat-drawer-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.ai-chat-log {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.ai-chat-footer {
  flex: 0 0 auto;
}

::v-deep .ai-chat-input textarea {
  max-height: 160px;
  overflow-y: auto !important;
}

.ai-chat-row {
  display: flex;
  margin: 8px 0;
}

.ai-chat-row-user {
  justify-content: flex-end;
}

.ai-chat-row-assistant {
  justify-content: flex-start;
}

.ai-chat-bubble {
  max-width: 90%;
}

.create-plan-ai-btn {
  min-width: 0;
  max-width: 100%;
}

::v-deep .create-plan-ai-btn .v-btn__content {
  white-space: normal;
  overflow-wrap: anywhere;
  text-align: center;
}
</style>
